import { foodBusinessCategories } from './enums';
import { BusinessFormData, BusinessStageType, MyBusinessSchema } from '../data/types';
import { resolveUsStateAbbreviation } from './helpers';

const emptyForm = (): BusinessFormData => ({
  businessName: '',
  businessCategory: -1,
  businessType: '',
  yearsOfOperation: '',
  description: '',
  businessStage: 'dream',
  logo: '',
  logoFile: null,
  cookingState: '',
  cookingCounty: '',
  cookingCity: '',
  cookingZipcode: '',
  otherLocation: '',
  kitchenTimeframe: '',
  kitchenBudget: '',
  kitchenFrequency: '',
  kitchenSize: '',
  kitchenEquipment: [],
  kitchenFeatures: [],
  maxChefs: '',
  paymentFrequency: '',
  kitchenType: '',
});

/** Map My Business stage vocabulary onto check-fit qualification stages. */
export const mapBusinessStageToQualification = (stage?: string): string => {
  if (stage === 'dream' || stage === 'idea') return 'idea';
  if (stage === 'build' || stage === 'launched') return 'launched';
  if (stage === 'scale' || stage === 'growing') return 'growing';
  return stage || '';
};

/** Map a saved business into the Add Business form, including city and ZIP. */
export const mapBusinessToFormData = (editingBusiness?: MyBusinessSchema | null): BusinessFormData => {
  if (!editingBusiness) return emptyForm();
  const businessCategoryIndex = foodBusinessCategories.findIndex(
    (cat: { category: string }) => cat.category === editingBusiness.business_category
  );
  return {
    businessName: editingBusiness.business_name,
    businessCategory: businessCategoryIndex,
    businessType: editingBusiness.business_type,
    yearsOfOperation: editingBusiness.years_of_operation,
    description: editingBusiness.description,
    businessStage: editingBusiness.business_stage as BusinessStageType,
    logo: editingBusiness.logo || '',
    logoFile: null,
    cookingState: editingBusiness.cooking_state,
    cookingCounty: editingBusiness.cooking_county,
    cookingCity: editingBusiness.cooking_city || '',
    cookingZipcode: editingBusiness.cooking_zipcode || '',
    otherLocation: editingBusiness.other_location || '',
    kitchenTimeframe: editingBusiness.kitchen_timeframe || '',
    kitchenBudget: editingBusiness.kitchen_budget || '',
    kitchenFrequency: editingBusiness.kitchen_frequency || '',
    kitchenSize: editingBusiness.kitchen_size || '',
    kitchenEquipment: editingBusiness.kitchen_equipment || [],
    kitchenFeatures: editingBusiness.kitchen_features || [],
    maxChefs: editingBusiness.max_chefs || '',
    paymentFrequency: editingBusiness.payment_frequency || '',
    kitchenType: editingBusiness.kitchen_type || '',
  };
};

/** Convert Add Business form values into the canonical MyBusinessSchema. */
export const mapBusinessFormToSchema = (
  data: BusinessFormData,
  username: string,
  extras?: Partial<MyBusinessSchema>
): MyBusinessSchema => {
  const category = foodBusinessCategories[data.businessCategory as number];
  return {
    username,
    business_name: data.businessName || '',
    business_category: category?.category || '',
    business_type: data.businessType || '',
    years_of_operation: data.yearsOfOperation || '',
    description: data.description || '',
    business_stage: data.businessStage || 'dream',
    logo: data.logo || '',
    cooking_state: data.cookingState || '',
    cooking_county: data.cookingCounty || '',
    cooking_city: data.cookingCity || '',
    cooking_zipcode: data.cookingZipcode || '',
    other_location: data.otherLocation || '',
    kitchen_timeframe: data.kitchenTimeframe || '',
    kitchen_budget: data.kitchenBudget || '',
    kitchen_frequency: data.kitchenFrequency || '',
    kitchen_size: data.kitchenSize || '',
    kitchen_equipment: data.kitchenEquipment || [],
    kitchen_features: data.kitchenFeatures || [],
    max_chefs: data.maxChefs || '',
    payment_frequency: data.paymentFrequency || '',
    kitchen_type: data.kitchenType || '',
    ...extras,
  };
};

/** Human-readable cooking location for cards and referrals. */
export const formatBusinessLocation = (business?: Partial<MyBusinessSchema> | null): string => {
  if (!business) return '';
  const city = (business.cooking_city || business.other_location || '').trim();
  const county = (business.cooking_county || '').trim();
  const state = resolveUsStateAbbreviation(business.cooking_state || '') || business.cooking_state || '';
  const zip = (business.cooking_zipcode || '').trim();
  return [city, county, state, zip].filter(Boolean).join(', ');
};

/** Allowlisted snapshot used on a check-fit referral (no menu, supplies, or sales). */
export const buildBusinessReferralSnapshot = (business: MyBusinessSchema) => ({
  business_id: business.id,
  business_name: business.business_name,
  business_type: business.business_type,
  business_stage: mapBusinessStageToQualification(business.business_stage),
  city: business.cooking_city || business.other_location || '',
  county: business.cooking_county || '',
  state: resolveUsStateAbbreviation(business.cooking_state) || business.cooking_state,
  zipcode: business.cooking_zipcode || '',
});
